import { CalendarIcon, Cross2Icon } from '@radix-ui/react-icons'
import { type CSSProperties, useEffect, useState } from 'react'
import { Link, useFetcher, useRevalidator } from 'react-router'
import {
	festivalParts,
	formatClock,
	formatDayMonth,
	formatShortWeekday,
	formatWeekday,
	isEventPassed,
	venueLabel,
} from '#app/domains/program/pure'
import { ticketLabel } from '#app/domains/tickets/pure'
import { formatNOK } from '#app/utils/formatNOK.tsx'
import { DoorPassDialog } from './DoorPassDialog.tsx'

export type WalletTicketStatus =
	| 'paid'
	| 'door'
	| 'unpaid'
	| 'free'
	| 'refunded'

export type WalletTicket = {
	id: string
	type: string
	name: string | null
	used: boolean
	status: WalletTicketStatus
	price: number | null
	/** cash registered so far; a ticket with money on it can't be removed */
	amountPaid?: number
}

export type WalletConcert = {
	event: {
		id: string
		title: string
		date: Date | string
		location: string | null
		host: string | null
	}
	/** the buyer's door pass for the concert — one QR for all its tickets;
	 * null when the viewer may not see door passes */
	passHash: string | null
	tickets: WalletTicket[]
}

const STATUS: Record<
	WalletTicketStatus,
	{ label: string; tone: 'good' | 'warn' | 'bad' | 'muted' }
> = {
	paid: { label: 'Betalt', tone: 'good' },
	free: { label: 'Gratis', tone: 'good' },
	door: { label: 'Betaling i døra', tone: 'warn' },
	unpaid: { label: 'Ikkje betalt', tone: 'bad' },
	refunded: { label: 'Refundert', tone: 'muted' },
}

const statusOf = (ticket: WalletTicket) =>
	ticket.used
		? ({ label: 'Brukt', tone: 'muted' } as const)
		: STATUS[ticket.status]

/** the tickets the door pass lets in */
const scannable = (concert: WalletConcert) =>
	concert.passHash == null
		? []
		: concert.tickets.filter((ticket) => ticket.status !== 'refunded')

/**
 * The buyer's tickets as a wallet: one ticket stub per concert (date on the
 * stub, the tickets and their status inside), the door pass — one QR for
 * all of the concert's tickets — one tap away, unpaid tickets removable, and
 * each concert into the calendar.
 */
export function TicketWallet({
	concerts,
	canEdit,
}: {
	concerts: WalletConcert[]
	/** the owner, or staff who may delete tickets: may remove unpaid ones */
	canEdit: boolean
}) {
	const [openEventId, setOpenEventId] = useState<string | null>(null)
	const open = concerts.find((concert) => concert.event.id === openEventId)

	return (
		<>
			<ol className="wl-list">
				{concerts.map((concert, i) => {
					const { event, tickets } = concert
					const passed = isEventPassed(event.date)
					const venue = venueLabel(event)
					return (
						<li
							key={event.id}
							className="wl-ticket"
							data-passed={passed ? '' : undefined}
							style={{ '--wl-i': i } as CSSProperties}
						>
							<div className="wl-card">
								<div className="wl-stub" aria-hidden="true">
									<span className="wl-stub-weekday">
										{formatShortWeekday(event.date).replace('.', '')}
									</span>
									<span className="wl-stub-day">
										{festivalParts(event.date).day}
									</span>
									<span className="wl-stub-time">
										{formatClock(event.date)}
									</span>
								</div>
								<div className="wl-body">
									<h2 className="wl-title">
										<Link
											to={`/events/${event.id}`}
											viewTransition
											style={{ viewTransitionName: `event-title-${event.id}` }}
										>
											{event.title}
										</Link>
									</h2>
									<p className="wl-meta">
										<time
											dateTime={new Date(event.date).toISOString()}
											className="sr-only"
										>
											{formatWeekday(event.date)} {formatDayMonth(event.date)}
											,{' '}
										</time>
										kl. {formatClock(event.date)}
										{venue ? ` · ${venue}` : ''}
									</p>
									<ul className="wl-lines">
										{tickets.map((ticket) => (
											<WalletLine
												key={ticket.id}
												ticket={ticket}
												label={ticketLabel(ticket, tickets)}
												canRemove={
													canEdit &&
													!passed &&
													!ticket.used &&
													!ticket.amountPaid &&
													(ticket.status === 'unpaid' ||
														ticket.status === 'door' ||
														ticket.status === 'free')
												}
											/>
										))}
									</ul>
									<div className="wl-actions">
										{passed ? (
											<span className="wl-done">Fullført</span>
										) : (
											<>
												{scannable(concert).length > 0 ? (
													<button
														type="button"
														className="wl-btn wl-btn-primary"
														onClick={() => setOpenEventId(event.id)}
													>
														Vis i døra
													</button>
												) : null}
												<a
													href={`/events/${event.id}/kalender.ics`}
													className="wl-btn"
												>
													<CalendarIcon aria-hidden="true" /> Kalender
												</a>
											</>
										)}
									</div>
								</div>
							</div>
						</li>
					)
				})}
			</ol>
			{open?.passHash ? (
				<DoorPassDialog
					eventTitle={open.event.title}
					passHash={open.passHash}
					lines={scannable(open).map((ticket) => ({
						label: ticketLabel(ticket, open.tickets),
						status: statusOf(ticket).label,
						tone: statusOf(ticket).tone,
					}))}
					open
					onClose={() => setOpenEventId(null)}
				/>
			) : null}
		</>
	)
}

function WalletLine({
	ticket,
	label,
	canRemove,
}: {
	ticket: WalletTicket
	label: string
	canRemove: boolean
}) {
	const fetcher = useFetcher<{ success?: boolean; error?: string }>()
	const removing = fetcher.state !== 'idle'
	// a refused removal (paid meanwhile, no rights) keeps the line, says why,
	// and reloads the page, which a refused action doesn't by itself: the
	// line may owe less now, or have lost its ×
	const refusal = fetcher.state === 'idle' ? fetcher.data?.error : undefined
	const { revalidate } = useRevalidator()
	useEffect(() => {
		if (refusal) void revalidate()
	}, [refusal, revalidate])
	const status = statusOf(ticket)
	return (
		<li className="wl-line" data-removing={removing ? '' : undefined}>
			<span className="wl-line-text">
				<span className="wl-line-label">{label}</span>
				{ticket.price != null && ticket.price > 0 ? (
					<span className="wl-line-price">{formatNOK(ticket.price)}</span>
				) : null}
			</span>
			<span className="wl-line-end">
				<span className="wl-status" data-tone={status.tone}>
					{status.label}
				</span>
				{canRemove ? (
					<fetcher.Form method="post" className="wl-remove">
						<input type="hidden" name="action" value="remove-ticket" />
						<input type="hidden" name="ticketId" value={ticket.id} />
						<button
							type="submit"
							disabled={removing}
							aria-label={`Fjern billetten «${label}»`}
						>
							<Cross2Icon aria-hidden="true" />
						</button>
					</fetcher.Form>
				) : null}
			</span>
			{refusal ? (
				<p className="wl-line-error" role="alert">
					{refusal}
				</p>
			) : null}
		</li>
	)
}
